import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ApiError, authApi } from "@/lib/api";
import { useAuth } from "@/lib/useAuth";
import { consumeOaRedirect, getOaCallbackUrl } from "@/lib/oaAuth";
import { Alert } from "@/components/ui/Alert";

type Phase = "loading" | "success" | "error";

export function OACallbackPage(): JSX.Element {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithSession } = useAuth();
  const [phase, setPhase] = useState<Phase>("loading");
  const [message, setMessage] = useState("正在完成 OA 登录…");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ticket = (params.get("ticket") || "").trim();
      const state = (params.get("state") || "").trim();
      if (!ticket) {
        setPhase("error");
        setMessage("OA 回调缺少 ticket，请重新登录。");
        return;
      }
      try {
        // service must match the URL registered at login-url time (without losing state param policy).
        // Backend validates host + path == /oa-callback; query string is part of CAS service string
        // only if we included it when requesting login-url. We use callback URL with state.
        const service = getOaCallbackUrl(state);
        const redirect = consumeOaRedirect(state);
        const res = await authApi.oaCallback(ticket, service);
        if (cancelled) return;
        loginWithSession({
          token: res.token,
          user_id: res.user_id,
          username: res.username,
          role: res.role,
        });
        setPhase("success");
        setMessage(`登录成功（${res.username}），正在进入系统…`);
        setTimeout(() => navigate(redirect, { replace: true }), 400);
      } catch (err) {
        if (cancelled) return;
        setPhase("error");
        if (err instanceof ApiError) {
          setMessage(err.message || "OA 登录失败");
        } else if (err instanceof Error) {
          setMessage(err.message);
        } else {
          setMessage("OA 登录失败，请重试");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [params, navigate, loginWithSession]);

  return (
    <div className="flex min-h-full items-center justify-center bg-zinc-50 px-4 py-12">
      <div className="w-full max-w-sm space-y-4 rounded-xl border border-zinc-200 bg-white p-6 shadow-sm text-center">
        <h1 className="text-xl font-semibold text-zinc-900">
          {phase === "loading" ? "OA 登录中" : phase === "success" ? "登录成功" : "登录失败"}
        </h1>
        {phase === "error" ? <Alert tone="error">{message}</Alert> : <p className="text-sm text-zinc-600">{message}</p>}
        {phase === "error" ? (
          <div className="flex flex-col gap-2">
            <Link
              to="/login"
              className="inline-flex h-10 items-center justify-center rounded-md bg-zinc-900 px-4 text-sm font-medium text-white hover:bg-zinc-800"
            >
              返回登录
            </Link>
          </div>
        ) : null}
      </div>
    </div>
  );
}
